import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  FitnessCenter as ExerciseIcon,
  Person as PersonIcon,
  PublicOutlined as GlobalIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import esLocale from 'date-fns/locale/es';
import StyledTextField from 'common/theme/fields/StyledTextfield';
import datePickerCalendarStyles from 'common/theme/fields/date-picker/datePickerCalendarStyles';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

const DEFAULT_EXERCISES = [];

function DynamicPerformanceModal({
  open,
  onClose,
  onSubmit,
  performance = null,
  clients = [],
  exercises = DEFAULT_EXERCISES,
  loading = false,
  lockedClient = null
}) {
  const theme = useTheme();
  const isEditing = Boolean(performance);
  // Cuando se crea desde el panel de un cliente, el cliente viene fijado y su selector se bloquea.
  const clientLocked = !isEditing && Boolean(lockedClient);
  const lockedClientName =
    lockedClient?.full_name ||
    `${lockedClient?.first_name || ''} ${lockedClient?.last_name || ''}`.trim();
  const editingClientName = performance?.client
    ? `${performance.client.first_name} ${performance.client.last_name}`
    : '';
  const readonlyClientName = isEditing ? editingClientName : lockedClientName;
  const { t } = useTranslation(['performances', 'buttons', 'validations']);
  const [formData, setFormData] = useState({
    client_id: '',
    exercise_id: '',
    period_start_date: new Date(),
    period_end_date: new Date(),
    observations: '',
    sets: []
  });

  const [selectedExercise, setSelectedExercise] = useState(null);
  const [errors, setErrors] = useState({});
  // Snapshot del formulario al abrir: "Guardar" queda deshabilitado hasta que haya cambios reales.
  const initialSnapshot = useRef('');

  const getParameterInfo = useCallback((type) => {
    const typeKey = type.toLowerCase();
    return {
      icon: t(`dynamicPerformances.parameters.${typeKey}.icon`, { ns: 'performances' }),
      label: t(`dynamicPerformances.parameters.${typeKey}.label`, { ns: 'performances' })
    };
  }, [t]);

  const addSet = useCallback(() => {
    setFormData((prev) => {
      const newSet = {
        set_number: prev.sets.length + 1,
        time_seconds: '',
        repetitions: '',
        weight_kg: '',
        distance_km: ''
      };
      return {
        ...prev,
        sets: [...prev.sets, newSet]
      };
    });
  }, []);

  // Pre-llenar datos cuando se abre en modo edición
  useEffect(() => {
    if (open && performance) {
      const prefilled = {
        client_id: performance.client?.id || '',
        exercise_id: performance.exercise?.id || '',
        period_start_date: performance.period_start_date ? new Date(performance.period_start_date) : new Date(),
        period_end_date: performance.period_end_date ? new Date(performance.period_end_date) : new Date(),
        observations: performance.observations || '',
        sets: performance.sets?.map((set) => ({
          set_number: set.set_number,
          time_seconds: set.time_seconds || '',
          repetitions: set.repetitions || '',
          weight_kg: set.weight_kg || '',
          distance_km: set.distance_km || ''
        })) || []
      };
      setFormData(prefilled);
      initialSnapshot.current = JSON.stringify(prefilled);
      if (performance.exercise) {
        const exercise = exercises.find((ex) => ex.id === performance.exercise.id);
        setSelectedExercise(exercise || performance.exercise);
      }
    } else if (open && !performance) {
      // Modo creación: preseleccionar el cliente bloqueado si viene del panel del cliente.
      setFormData((prev) => ({ ...prev, client_id: lockedClient?.id || '' }));
    } else if (!open) {
      setFormData({
        client_id: '',
        exercise_id: '',
        period_start_date: new Date(),
        period_end_date: new Date(),
        observations: '',
        sets: []
      });
      setSelectedExercise(null);
      setErrors({});
    }
  }, [open, performance, exercises, lockedClient]);

  useEffect(() => {
    if (isEditing) return; // En modo edición, el pre-fill ya maneja selectedExercise y sets
    if (formData.exercise_id && exercises) {
      const exercise = exercises.find((ex) => ex.id === formData.exercise_id);
      setSelectedExercise(exercise);

      if (exercise && exercise.parameters.length > 0) {
        if (formData.sets.length === 0) {
          addSet();
        }
      } else {
        setFormData((prev) => ({ ...prev, sets: [] }));
      }
    }
  }, [formData.exercise_id, exercises, formData.sets.length, addSet, isEditing]);

  const removeSet = (index) => {
    setFormData((prev) => ({
      ...prev,
      sets: prev.sets.filter((_, i) => i !== index).map((set, i) => ({
        ...set,
        set_number: i + 1
      }))
    }));
  };

  const updateSet = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sets: prev.sets.map((set, i) =>
        i === index ? { ...set, [field]: value } : set
      )
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!isEditing && !formData.client_id) {
      newErrors.client_id = t('required', { ns: 'validations' });
    }

    if (!isEditing && !formData.exercise_id) {
      newErrors.exercise_id = t('required', { ns: 'validations' });
    }

    if (!formData.period_start_date) {
      newErrors.period_start_date = t('required', { ns: 'validations' });
    }

    if (!formData.period_end_date) {
      newErrors.period_end_date = t('required', { ns: 'validations' });
    }

    if (formData.period_start_date && formData.period_end_date) {
      if (new Date(formData.period_end_date) < new Date(formData.period_start_date)) {
        newErrors.period_end_date = t('invalidDateRange', { ns: 'validations' });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const sets = formData.sets
      .filter((set) =>
        set.time_seconds ||
        set.repetitions ||
        set.weight_kg ||
        set.distance_km
      )
      .map((set) => ({
        set_number: set.set_number,
        time_seconds: set.time_seconds ? parseInt(set.time_seconds, 10) : null,
        repetitions: set.repetitions ? parseInt(set.repetitions, 10) : null,
        weight_kg: set.weight_kg ? parseFloat(set.weight_kg) : null,
        distance_km: set.distance_km ? parseFloat(set.distance_km) : null
      }));

    const periodStart = formatDate(formData.period_start_date);
    const periodEnd = formatDate(formData.period_end_date);

    if (isEditing) {
      onSubmit({
        period_start_date: periodStart,
        period_end_date: periodEnd,
        observations: formData.observations || null,
        sets
      });
    } else {
      onSubmit({
        client_id: formData.client_id,
        exercise_id: formData.exercise_id,
        period_start_date: periodStart,
        period_end_date: periodEnd,
        observations: formData.observations || null,
        sets
      });
    }
  };

  const exerciseParameters = selectedExercise?.parameters?.map((p) => p.parameter_type) || [];
  const dirty = JSON.stringify(formData) !== initialSnapshot.current;
  const saveDisabled = loading || (isEditing && !dirty);
  const dpSlotProps = (key) => ({
    popper: { sx: datePickerCalendarStyles(theme) },
    mobilePaper: { sx: datePickerCalendarStyles(theme) },
    textField: { fullWidth: true, error: !!errors[key], helperText: errors[key] }
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'surface.content', backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
            <ExerciseIcon sx={{ color: 'gold.main', fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              {isEditing
                ? t('dynamicPerformances.modal.editTitle', { ns: 'performances', defaultValue: 'Editar Rendimiento' })
                : t('dynamicPerformances.modal.title', { ns: 'performances' })}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Grid container spacing={3}>
            {/* Client Selection */}
            <Grid item xs={12}>
              {isEditing || clientLocked ? (
                <TextField
                  fullWidth
                  label={t('dynamicPerformances.table.client', { ns: 'performances' })}
                  value={readonlyClientName}
                  disabled
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ fontSize: 18, color: '#F9BD4E', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                    },
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .Mui-disabled': { WebkitTextFillColor: 'rgba(255, 255, 255, 0.7) !important' }
                  }}
                />
              ) : (
                <FormControl fullWidth error={!!errors.client_id}>
                  <InputLabel sx={{ color: 'text.secondary' }}>
                    {t('dynamicPerformances.table.client', { ns: 'performances' })}
                  </InputLabel>
                  <Select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    label={t('dynamicPerformances.table.client', { ns: 'performances' })}
                    sx={{
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: errors.client_id ? '#f44336' : 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#F9BD4E'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'surface.content',
                          '& .MuiMenuItem-root': {
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'rgba(249, 189, 78, 0.1)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(249, 189, 78, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(249, 189, 78, 0.3)'
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                        <Typography>{t('dynamicPerformances.modal.selectClient', { ns: 'performances' })}</Typography>
                      </Stack>
                    </MenuItem>
                    {clients?.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                          <Typography>{client.first_name} {client.last_name}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.client_id && (
                    <Typography variant="caption" color="#f44336" sx={{ mt: 0.5, ml: 2 }}>
                      {errors.client_id}
                    </Typography>
                  )}
                </FormControl>
              )}
            </Grid>

            {/* Exercise Selection */}
            <Grid item xs={12}>
              {isEditing ? (
                <TextField
                  fullWidth
                  label={t('dynamicPerformances.table.exercise', { ns: 'performances' })}
                  value={performance?.exercise?.name || ''}
                  disabled
                  InputProps={{
                    startAdornment: <ExerciseIcon sx={{ fontSize: 18, color: '#F9BD4E', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'text.primary',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                    },
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .Mui-disabled': { WebkitTextFillColor: 'rgba(255, 255, 255, 0.7) !important' }
                  }}
                />
              ) : (
                <FormControl fullWidth error={!!errors.exercise_id}>
                  <InputLabel sx={{ color: 'text.secondary' }}>
                    {t('dynamicPerformances.table.exercise', { ns: 'performances' })}
                  </InputLabel>
                  <Select
                    value={formData.exercise_id}
                    onChange={(e) => setFormData({ ...formData, exercise_id: e.target.value })}
                    label={t('dynamicPerformances.table.exercise', { ns: 'performances' })}
                    sx={{
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: errors.exercise_id ? '#f44336' : 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#F9BD4E'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'surface.content',
                          '& .MuiMenuItem-root': {
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'rgba(249, 189, 78, 0.1)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(249, 189, 78, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(249, 189, 78, 0.3)'
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ExerciseIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                        <Typography>{t('dynamicPerformances.modal.selectExercise', { ns: 'performances' })}</Typography>
                      </Stack>
                    </MenuItem>
                    {exercises?.map((exercise) => (
                      <MenuItem key={exercise.id} value={exercise.id}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                          <ExerciseIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                          <Typography sx={{ flex: 1 }}>{exercise.name}</Typography>
                          {exercise.is_otrixverse && (
                            <Chip
                              size="small"
                              icon={<GlobalIcon sx={{ fontSize: 14 }} />}
                              label="OTRIXVERSE"
                              sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700, height: 20, '& .MuiChip-icon': { color: 'gold.main' } }}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.exercise_id && (
                    <Typography variant="caption" color="#f44336" sx={{ mt: 0.5, ml: 2 }}>
                      {errors.exercise_id}
                    </Typography>
                  )}
                </FormControl>
              )}

              {/* Show exercise parameters */}
              {selectedExercise && (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedExercise.parameters.map((param) => {
                      const paramInfo = getParameterInfo(param.parameter_type);
                      return (
                        <Chip
                          key={param.id}
                          label={`${paramInfo.icon} ${paramInfo.label}`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(249, 189, 78, 0.2)',
                            color: '#F9BD4E',
                            border: '1px solid rgba(249, 189, 78, 0.3)'
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Grid>

            {/* Period Dates */}
            <Grid item xs={6}>
              <DatePicker
                label={t('dynamicPerformances.modal.periodStart', { ns: 'performances' })}
                value={formData.period_start_date}
                onChange={(newValue) => setFormData({ ...formData, period_start_date: newValue })}
                slots={{ textField: StyledTextField }}
                slotProps={dpSlotProps('period_start_date')}
              />
            </Grid>

            <Grid item xs={6}>
              <DatePicker
                label={t('dynamicPerformances.modal.periodEnd', { ns: 'performances' })}
                value={formData.period_end_date}
                onChange={(newValue) => setFormData({ ...formData, period_end_date: newValue })}
                slots={{ textField: StyledTextField }}
                slotProps={dpSlotProps('period_end_date')}
              />
            </Grid>

            {/* Observations */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('dynamicPerformances.modal.observations', { ns: 'performances' })}
                placeholder={t('dynamicPerformances.modal.observationsPlaceholder', { ns: 'performances' })}
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'text.primary',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary'
                  }
                }}
              />
            </Grid>

            {/* Sets Section - Show if exercise has any parameters */}
            {selectedExercise && exerciseParameters.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ bgcolor: 'text.disabled', my: 2 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" color="text.primary">
                      {t('dynamicPerformances.modal.sets', { ns: 'performances' })}
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addSet}
                      variant="outlined"
                      size="small"
                      sx={{
                        color: '#F9BD4E',
                        borderColor: '#F9BD4E',
                        '&:hover': {
                          borderColor: '#ffd166',
                          bgcolor: 'rgba(249, 189, 78, 0.1)'
                        }
                      }}
                    >
                      {t('dynamicPerformances.modal.addSet', { ns: 'performances' })}
                    </Button>
                  </Stack>
                </Grid>

                {formData.sets.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)' }}>
                      {t('dynamicPerformances.modal.atLeastOneSet', { ns: 'performances' })}
                    </Alert>
                  </Grid>
                )}

                {formData.sets.map((set, index) => (
                  <Grid item xs={12} key={`set-${set.set_number}`}>
                    <Card
                      sx={{
                        bgcolor: 'rgba(26, 26, 46, 0.6)',
                        border: '1px solid rgba(249, 189, 78, 0.3)',
                        borderRadius: 2
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" color="text.primary" fontWeight="bold">
                            {t('dynamicPerformances.modal.setNumber', { number: set.set_number, ns: 'performances' })}
                          </Typography>
                          <IconButton
                            onClick={() => removeSet(index)}
                            size="small"
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>

                        <Grid container spacing={2}>
                          {exerciseParameters.includes('TIME') && (
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('dynamicPerformances.modal.time', { ns: 'performances' })}
                                value={set.time_seconds}
                                onChange={(e) => updateSet(index, 'time_seconds', e.target.value)}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'text.primary',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                                  },
                                  '& .MuiInputLabel-root': { color: 'text.secondary' }
                                }}
                              />
                            </Grid>
                          )}

                          {exerciseParameters.includes('REPS') && (
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('dynamicPerformances.modal.reps', { ns: 'performances' })}
                                value={set.repetitions}
                                onChange={(e) => updateSet(index, 'repetitions', e.target.value)}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'text.primary',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                                  },
                                  '& .MuiInputLabel-root': { color: 'text.secondary' }
                                }}
                              />
                            </Grid>
                          )}

                          {exerciseParameters.includes('WEIGHT') && (
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('dynamicPerformances.modal.weight', { ns: 'performances' })}
                                value={set.weight_kg}
                                onChange={(e) => updateSet(index, 'weight_kg', e.target.value)}
                                size="small"
                                inputProps={{ step: '0.5' }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'text.primary',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                                  },
                                  '& .MuiInputLabel-root': { color: 'text.secondary' }
                                }}
                              />
                            </Grid>
                          )}

                          {exerciseParameters.includes('DISTANCE') && (
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('dynamicPerformances.modal.distance', { ns: 'performances' })}
                                value={set.distance_km}
                                onChange={(e) => updateSet(index, 'distance_km', e.target.value)}
                                size="small"
                                inputProps={{ step: '0.1' }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: 'text.primary',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                                  },
                                  '& .MuiInputLabel-root': { color: 'text.secondary' }
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </>
            )}

            {/* Info for exercises without parameters */}
            {selectedExercise && exerciseParameters.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)' }}>
                  {t('dynamicPerformances.modal.noSetsMessage', { ns: 'performances' })}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2, color: 'text.primary', borderColor: 'hairline.strong', fontWeight: 700, textTransform: 'none' }}
          >
            {t('cancel', { ns: 'buttons' })}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saveDisabled}
            sx={{ ...premiumButtonSx, py: 1.1, px: 3, fontSize: '0.95rem' }}
          >
            {loading ? t('dynamicPerformances.loading', { ns: 'performances' }) : t('save', { ns: 'buttons' })}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

DynamicPerformanceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  performance: PropTypes.object,
  clients: PropTypes.array,
  exercises: PropTypes.array,
  loading: PropTypes.bool,
  lockedClient: PropTypes.object
};

export default DynamicPerformanceModal;
